export interface CalendarAppointment {
  id?: string;
  when?: string;
  /** Confirmed appointment duration in minutes. Never use a default duration. */
  duration?: number;
  practiceName?: string;
  practiceAddress?: string;
}

const timeZone = 'Europe/Zurich';
const practiceClock = new Intl.DateTimeFormat('en-GB', {
  timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});
const invalidData = () => new Error('Der Kalendereintrag konnte nicht erstellt werden. Buchungsnummer, Terminzeit oder bestätigte Dauer fehlen oder sind ungültig. Bitte öffnen Sie die aktuelle Terminbestätigung erneut.');

const localClock = (instant: number) => {
  const parts = Object.fromEntries(practiceClock.formatToParts(new Date(instant)).map(({ type, value }) => [type, value]));
  return Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
};

/** Offset-free API timestamps are Zurich wall time, regardless of the device zone. */
export function calendarInstant(when: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|[+-]\d{2}:?\d{2})?$/i.exec(when);
  if (!match) throw invalidData();
  const [, year, month, day, hour, minute, second = '0', fraction = '', zone] = match;
  const parts = [+year, +month - 1, +day, +hour, +minute, +second];
  const wall = Date.UTC(parts[0], parts[1], parts[2], parts[3], parts[4], parts[5]);
  const date = new Date(wall);
  if (+year < 1000 || date.getUTCFullYear() !== parts[0] || date.getUTCMonth() !== parts[1]
    || date.getUTCDate() !== parts[2] || date.getUTCHours() !== parts[3]
    || date.getUTCMinutes() !== parts[4] || date.getUTCSeconds() !== parts[5]) throw invalidData();
  const milliseconds = +(fraction.padEnd(3, '0'));
  if (zone) {
    if (zone.toUpperCase() === 'Z') return wall + milliseconds;
    const digits = zone.slice(1).replace(':', '');
    const hours = +digits.slice(0, 2), minutes = +digits.slice(2);
    if (hours > 23 || minutes > 59) throw invalidData();
    return wall - (zone[0] === '+' ? 1 : -1) * (hours * 60 + minutes) * 60000 + milliseconds;
  }
  // Sample both sides of a possible clock change. Reject nonexistent spring times;
  // for repeated autumn times use the first occurrence (RFC 5545, section 3.3.5).
  const candidates = [-86400000, 0, 86400000].map(delta => {
    const sample = wall + delta;
    return wall - (localClock(sample) - sample);
  }).filter(candidate => localClock(candidate) === wall).sort((a, b) => a - b);
  if (!candidates.length) throw invalidData();
  return candidates[0] + milliseconds;
}

export const escapeCalendarText = (text: string) => Array.from(text).filter(character => {
  const code = character.charCodeAt(0);
  return (code >= 32 && code !== 127) || [9, 10, 13].includes(code);
}).join('').replace(/\\/g, '\\\\')
  .replace(/\r\n|\r|\n/g, '\\n').replace(/;/g, '\\;').replace(/,/g, '\\,');

/** Fold at 75 UTF-8 octets, without splitting a multibyte character. */
const foldLine = (line: string) => {
  let result = '', octets = 0;
  for (const character of Array.from(line)) {
    const code = character.codePointAt(0)!;
    const size = code <= 0x7f ? 1 : code <= 0x7ff ? 2 : code <= 0xffff ? 3 : 4;
    if (octets + size > 75) { result += '\r\n '; octets = 1; }
    result += character;
    octets += size;
  }
  return result;
};
const utc = (date: Date) => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

export function createAppointmentCalendar(appointment: CalendarAppointment, now = new Date()): string {
  const id = typeof appointment.id === 'string' ? appointment.id.trim() : '';
  if (!id || typeof appointment.when !== 'string' || !appointment.when || typeof appointment.duration !== 'number'
    || !Number.isFinite(appointment.duration) || appointment.duration <= 0
    || !Number.isSafeInteger(appointment.duration * 60)) throw invalidData();
  const start = calendarInstant(appointment.when);
  const end = start + appointment.duration * 60000;
  if (!Number.isFinite(end) || end <= start || Number.isNaN(new Date(end).getTime())
    || new Date(end).getUTCFullYear() > 9999) throw invalidData();
  // Existing practice address: public/texts/directions.txt and DirectionsPage.tsx.
  // No practice name is invented when the confirmed data does not supply one.
  const location = [appointment.practiceName, appointment.practiceAddress || 'Moserstrasse 27, Schaffhausen']
    .filter(Boolean).join(', ');
  let uid: string;
  try { uid = encodeURIComponent(id); } catch { throw invalidData(); }
  // UTC event times preserve exact instants even across DST folds. A VTIMEZONE
  // component is not required for UTC values (RFC 5545, section 3.2.19).
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//RiviDent//Patientenportal//DE',
    'CALSCALE:GREGORIAN', `X-WR-TIMEZONE:${timeZone}`, 'BEGIN:VEVENT',
    `UID:rivident-${uid}@rivident.com`, `DTSTAMP:${utc(now)}`,
    `DTSTART:${utc(new Date(start))}`, `DTEND:${utc(new Date(end))}`,
    'SUMMARY:Zahnarzttermin', `LOCATION:${escapeCalendarText(location)}`,
    'STATUS:CONFIRMED', 'CLASS:PRIVATE', 'TRANSP:OPAQUE',
    'BEGIN:VALARM', 'ACTION:DISPLAY', 'DESCRIPTION:Zahnarzttermin', 'TRIGGER:-PT24H',
    'END:VALARM', 'END:VEVENT', 'END:VCALENDAR',
  ].map(foldLine).join('\r\n') + '\r\n';
}
